import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPut } from '../../lib/api'
import { ArrowLeft, Bell, CheckCircle, AlertCircle } from 'lucide-react'

interface NotificationPreferences {
  id: string
  userId: string
  enabled: boolean
  emailNotifications: boolean
  days90: boolean
  days60: boolean
  days30: boolean
  days14: boolean
  days7: boolean
  days3: boolean
  days1: boolean
}

const REMINDER_OPTIONS: { key: keyof NotificationPreferences; label: string }[] = [
  { key: 'days90', label: '90 days' },
  { key: 'days60', label: '60 days' },
  { key: 'days30', label: '30 days' },
  { key: 'days14', label: '14 days' },
  { key: 'days7', label: '7 days' },
  { key: 'days3', label: '3 days' },
  { key: 'days1', label: '1 day' },
]

export default function NotificationSettingsPage() {
  const queryClient = useQueryClient()
  const [success, setSuccess] = useState(false)
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['/api/notifications/preferences'],
    queryFn: () => apiGet<{ preferences: NotificationPreferences }>('/notifications/preferences'),
  })

  useEffect(() => {
    if (data?.preferences && !prefs) {
      setPrefs(data.preferences)
    }
  }, [data])

  const saveMutation = useMutation({
    mutationFn: (updates: Partial<NotificationPreferences>) =>
      apiPut('/notifications/preferences', updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/preferences'] })
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    },
  })

  const handleToggleEnabled = () => {
    if (!prefs) return
    const updated = { ...prefs, enabled: !prefs.enabled }
    setPrefs(updated)
    saveMutation.mutate({ enabled: updated.enabled })
  }

  const handleToggleDay = (key: keyof NotificationPreferences) => {
    if (!prefs) return
    const updated = { ...prefs, [key]: !prefs[key] }
    setPrefs(updated)
    saveMutation.mutate({ [key]: updated[key] })
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/profile" className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">Notification Settings</h1>
          <p className="text-sm text-gray-500">Manage how you receive alerts</p>
        </div>
        {success && (
          <span className="inline-flex items-center gap-1 text-sm text-green-600">
            <CheckCircle className="w-4 h-4" />
            Saved
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : isError || !prefs ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Unable to load notification settings</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Bell className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Notifications</p>
                  <p className="text-sm text-gray-500">Enable or disable all notifications</p>
                </div>
              </div>
              <button
                onClick={handleToggleEnabled}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  prefs.enabled ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    prefs.enabled ? 'left-7' : 'left-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {prefs.enabled && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Reminder Timing</h2>
              <p className="text-sm text-gray-500 mb-4">
                Choose when to receive expiration reminders before your certifications expire
              </p>
              <div className="flex flex-wrap gap-2">
                {REMINDER_OPTIONS.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => handleToggleDay(key)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      prefs[key]
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500">
              Push notifications are delivered to your mobile device through the app. 
              Email reminders are sent to the email address on your account.
              Reminders are sent at 11:00 AM in your local timezone.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
